import { useEffect, useState } from "react";
import { Video, ExternalLink, Bell } from "lucide-react";
import { portalApi, apiError } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuth";
import LiveButton from "./LiveButton";
import { toast } from "sonner";

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" });
const fmtTime = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

export default function Calls() {
  const { user } = usePortalAuth();
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reminding, setReminding] = useState(null);
  useEffect(() => { portalApi.get("/portal/calls").then((r) => setCalls(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const remind = async (id) => {
    setReminding(id);
    try {
      const { data } = await portalApi.post(`/portal/calls/${id}/remind`);
      toast.success(`Reminder sent to ${data.recipients} member${data.recipients === 1 ? "" : "s"}${data.sms_enabled ? " (email + SMS)" : " (email)"}.`);
    } catch (e) { toast.error(apiError(e)); } finally { setReminding(null); }
  };

  const now = new Date();
  const upcoming = calls.filter((c) => new Date(c.starts_at) >= now);
  const past = calls.filter((c) => new Date(c.starts_at) < now);
  const isAdmin = user?.role === "admin";

  const Row = ({ c, isPast }) => (
    <div data-testid={`call-${c.id}`} className={`flex flex-col sm:flex-row sm:items-center gap-4 justify-between border border-[#E0D8C9] rounded-xl p-6 ${isPast ? "opacity-60 bg-white/40" : "bg-white/60"}`}>
      <div>
        <div className="flex items-center gap-2">
          <h3 className="font-serif-display text-2xl">{c.title}</h3>
          {c.live && <span data-testid={`live-badge-${c.id}`} className="inline-flex items-center gap-1 text-[10px] tracking-widest uppercase text-[#A9724E] bg-[#EAE3D9] rounded-full px-2 py-0.5">In-app room</span>}
        </div>
        <p className="text-sm text-[#A9724E] font-body mt-1">{fmt(c.starts_at)}{c.ends_at ? ` – ${fmtTime(c.ends_at)}` : ""}</p>
        {c.description && <p className="text-[#5E4B3C] font-body mt-2">{c.description}</p>}
      </div>
      {!isPast && (
        <div className="shrink-0 flex items-center gap-2">
          {isAdmin && (
            <button data-testid={`remind-${c.id}`} onClick={() => remind(c.id)} disabled={reminding === c.id}
              className="inline-flex items-center gap-2 rounded-full border border-[#D8CEBE] hover:bg-[#EAE3D9] text-[#5E4B3C] px-4 py-3 text-sm transition-colors disabled:opacity-50">
              <Bell className="w-4 h-4" strokeWidth={1.5} />{reminding === c.id ? "Sending…" : "Remind members"}
            </button>
          )}
          {c.live ? (
            <LiveButton scope="gathering" targetId={c.id} title={c.title} label="Join the gathering" testid={`gathering-join-${c.id}`}
              className="inline-flex items-center gap-2 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-3 text-sm tracking-wide transition-colors" />
          ) : c.zoom_url ? (
            <a data-testid={`join-${c.id}`} href={c.zoom_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#A9724E] hover:bg-[#925C3B] text-[#F4EFE6] px-6 py-3 text-sm tracking-wide transition-colors">
              <Video className="w-4 h-4" strokeWidth={1.5} />Join on Zoom <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
            </a>
          ) : null}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="portal-calls">
      <p className="tracking-overline text-xs uppercase text-[#A9724E] mb-3">Come together</p>
      <h1 className="font-serif-display font-light text-4xl md:text-5xl mb-10">Gatherings</h1>
      {loading ? <p className="text-[#8a7a6a] font-body">Loading…</p> : calls.length === 0 ? (
        <p className="text-[#8a7a6a] font-body">No calls scheduled yet.</p>
      ) : (
        <div className="max-w-3xl space-y-8">
          {upcoming.length > 0 && <div className="space-y-4">{upcoming.map((c) => <Row key={c.id} c={c} />)}</div>}
          {past.length > 0 && (
            <div>
              <p className="tracking-overline text-xs uppercase text-[#8a7a6a] mb-4">Past</p>
              <div className="space-y-4">{past.map((c) => <Row key={c.id} c={c} isPast />)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
